/**
 * The inbox's search parameters — `?tab=`, `?form=` and `?q=` — on their own, so the
 * route file can declare them without importing the page.
 *
 * A route's `validateSearch` stays in the main chunk when its component is split off
 * (`vite.config.ts` `autoCodeSplitting`), and it takes the whole module it is imported
 * from with it: declared beside `SubmissionsPage`, the inbox and everything it imports
 * shipped with the shell. Here it costs the schema and nothing else
 * (`pages/blog/search.ts` is the same move).
 */

import { FormKeySchema } from '@fareast/shared'
import { z } from 'zod'

import { routeSearch } from '../../search/useSearchState.js'

/** The inbox's three tabs, as the address writes them. */
export const INBOX_TABS = ['open', 'handled', 'spam'] as const
export type InboxTab = (typeof INBOX_TABS)[number]

/**
 * The longest search the address keeps — `FormSubmissionsQuerySchema`'s `q` — and the
 * longest the inbox's search box takes.
 */
export const INBOX_SEARCH_MAX = 200

/**
 * What the inbox's address carries: the tab (Open when absent), the form, the words.
 * Every field is optional, so the bare address is the open inbox of every form.
 */
export const SubmissionsSearchSchema = z.object({
  tab: z.enum(INBOX_TABS).optional(),
  form: FormKeySchema.optional(),
  q: z.string().trim().min(1).max(INBOX_SEARCH_MAX).optional(),
})

export type SubmissionsSearch = z.infer<typeof SubmissionsSearchSchema>

/**
 * The route's `validateSearch`. Through `routeSearch`, so a value somebody typed falls
 * back to the open inbox rather than to an error page.
 */
export const parseSubmissionsSearch = routeSearch(SubmissionsSearchSchema)
